<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { $supplierList } from '@/api/users'
import { uniqBy } from 'lodash'
import { useDebounceFn } from '@vueuse/core'
import type { SupplierSelect } from '@/models/supplier'

interface IProps {
  modelValue?: any,
  label?: string,
  division?: any,
  /** Array of division identifier strings for multi-filtering */
  divisions?: string[] | null,
  selectOptions?: any[] | null,
  /**
   * Optional array of supplier IDs that should be disabled for selection.
   * If null or undefined, no suppliers will be disabled.
   */
  disabledSuppliers?: number[] | null,
  brandingCampaignsOnly?: boolean,
  wfCampaignsOnly?: boolean,
  kopCampaignsOnly?: boolean,
  retailCampaignsOnly?: boolean,
  activeCampaignsOnly?: boolean | null,
  viewAllSelectedValues?: boolean,
  withSignCatalogueIds?: boolean,
  projectSuppliersOnly?: boolean
  crSuppliersOnly?: boolean
  projectId?: number
  crId?: number
}

const props = withDefaults(defineProps<IProps>(), {
  modelValue: () => ([]),
  label: 'Suppliers',
  division: undefined,
  divisions: null,
  selectOptions: null,
  disabledSuppliers: null,
  brandingCampaignsOnly: false,
  wfCampaignsOnly: false,
  kopCampaignsOnly: false,
  retailCampaignsOnly: false,
  activeCampaignsOnly: null,
  viewAllSelectedValues: false,
  withSignCatalogueIds: false,
  projectSuppliersOnly: undefined,
  crSuppliersOnly: undefined,
  projectId: undefined,
  crId: undefined,
})

interface CustomEvents {
  'update:modelValue': (newValue: any) => void;
}

const emit = defineEmits<CustomEvents>()

const suppliers = ref<SupplierSelect[]>([])
const suppliersCurrentPage = ref<number>(1)
const suppliersPerPage: number = 10
const suppliersSearchStr = ref<string>('')
const isNextSuppliersPageExist = ref<boolean>(false)
const suppliersLoading = ref<boolean>(false)

const supplierOptions = computed(() => {
  if (props.selectOptions) {
    return props.selectOptions
  } else {
    return suppliers.value
  }
})

const getSuppliers = async () => {
  suppliersLoading.value = true

  const payloadDivision = props.division !== undefined ? props.division : undefined

  const payload = {
    supplier_name__icontains: suppliersSearchStr.value,
    division: payloadDivision,
    per_page: suppliersPerPage,
    page: suppliersCurrentPage.value,
    with_sign_catalogue_ids: props.withSignCatalogueIds || undefined,

    project_suppliers_only: props.projectSuppliersOnly,
    cr_suppliers_only: props.crSuppliersOnly,
    project_id: props.projectId,
    cr_id: props.crId
  } as any

  // Add multi-division filtering if the array is provided and contains elements
  if (props.divisions && props.divisions.length > 0) {
    payload.division__in = props.divisions.join(',')
  }

  if (props.brandingCampaignsOnly) {
    props.activeCampaignsOnly ? payload.active_branding_campaigns_only = true : payload.inactive_branding_campaigns_only = true
  } else if (props.wfCampaignsOnly) {
    props.activeCampaignsOnly ? payload.active_wf_campaigns_only = true : payload.inactive_wf_campaigns_only = true
  } else if (props.kopCampaignsOnly) {
    props.activeCampaignsOnly ? payload.active_kop_campaigns_only = true : payload.inactive_kop_campaigns_only = true
  } else if (props.retailCampaignsOnly) {
    props.activeCampaignsOnly ? payload.active_retail_campaigns_only = true : payload.inactive_retail_campaigns_only = true
  } else if (props.activeCampaignsOnly !== null) {
    props.activeCampaignsOnly ? payload.active_campaigns_only = true : payload.inactive_campaigns_only = true
  }
  try {
    const res = await $supplierList(payload)

    // Append the new suppliers to the existing list
    // Use Lodash's uniqBy to merge arrays while ensuring uniqueness based on 'id'
    suppliers.value = uniqBy([...suppliers.value, ...res.data.results], 'id')
    isNextSuppliersPageExist.value = !!res.data.next
  }
  catch (e) {
    console.log(e)
  }
  finally {
    suppliersLoading.value = false
  }
}

const onFocusSupplierSelect = async () => {
  if (!suppliers.value.length && !props.selectOptions) {
    suppliers.value = []
    suppliersCurrentPage.value = 1
    await getSuppliers()
  }
}

const onLoadMoreSuppliers = async () => {
  suppliersCurrentPage.value++ // Increment the current page to load the next page of suppliers
  await getSuppliers()
}

const clearAndGetSuppliers = (searchStr: string) => {
  suppliers.value = []
  suppliersCurrentPage.value = 1 // Set current page => 1 to load initial first page of suppliers
  suppliersSearchStr.value = searchStr
  getSuppliers()
}

const handleSupplierSearch = useDebounceFn((searchStr: string) => {
  if (!props.selectOptions) {
    clearAndGetSuppliers(searchStr)
  }
}, 500)

const updateSuppliersList = () => {
  suppliers.value = []
  suppliersCurrentPage.value = 1
  getSuppliers()
}

watch(() => props.division, () => {
  updateSuppliersList()
})

// Deep watcher targeting structural additions/removals inside divisions prop array
watch(() => props.divisions, () => {
  updateSuppliersList()
}, { deep: true })

watch(() => props.projectId, () => {
  updateSuppliersList()
})

watch(() => props.crId, () => {
  updateSuppliersList()
})

defineExpose({ clearAndGetSuppliers })
</script>

<template>
  <VAutocomplete
    :model-value="modelValue"
    :items="[...supplierOptions]"
    return-object
    item-title="supplier_name"
    item-value="id"
    :label="label"
    density="compact"
    :no-filter="!selectOptions"
    multiple
    clearable
    @update:modelValue="newValue => $emit('update:modelValue', newValue)"
    @update:focused="onFocusSupplierSelect"
    @update:search="handleSupplierSearch"
  >
    <template
      v-if="disabledSuppliers"
      #item="{ item, index, props }"
    >
      <VListItem
        v-bind="props"
        :disabled="!!disabledSuppliers ? disabledSuppliers.includes(item?.value) : false"
      />
    </template>
    <template #selection="{ item, index }">
      <div class="d-flex align-center">
        <template v-if="viewAllSelectedValues">
          <span>{{ item.title }}</span>
          <span v-if="index <= modelValue?.length - 2">
            ,
          </span>
        </template>
        <template v-else>
          <v-chip v-if="index < 4">
            <span>{{ item.title }}</span>
          </v-chip>
          <span
            v-if="index === 4"
            class="text-grey text-caption align-self-center"
          >
            (+{{ modelValue?.length - 4 }} others)
          </span>
        </template>
      </div>
    </template>
    <template #no-data>
      <div
        v-if="!!suppliersLoading"
        class="d-flex justify-center align-center"
      >
        Loading...
        <VProgressCircular
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </div>
      <div
        v-else
        class="d-flex justify-center align-center"
      >
        Suppliers not found
      </div>
    </template>
    <template
      v-if="isNextSuppliersPageExist"
      #append-item
    >
      <VBtn
        color="default"
        block
        :disabled="!!suppliersLoading"
        @click="onLoadMoreSuppliers"
      >
        Load more...
        <VProgressCircular
          v-if="!!suppliersLoading"
          class="margin-left-5"
          size="22"
          indeterminate
          color="white"
        />
      </VBtn>
    </template>
  </VAutocomplete>
</template>

<style lang="scss">

</style>
